/**
 * Google Ads Conversion Tracking Utility (PRODUCTION ONLY)
 * 
 * This utility ensures conversion events are tracked properly for Google Ads
 * Performance Max campaigns. It prevents duplicate tracking and only fires
 * on successful form submissions on PRODUCTION domains.
 * 
 * Google Ads Configuration:
 * - Conversion ID: AW-18389880536
 * - Conversion Action: Contact
 * - Conversion Label: MUZ6CPC7op4dENid_cBE
 * 
 * Production Domains:
 * - shippzy.ca
 * - www.shippzy.ca
 */

// Track successful form submissions to prevent duplicates
const trackedSubmissions = new Set();

/**
 * Check if current environment is production
 * @returns {boolean} True if on production domain
 */
const isProductionDomain = () => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname === 'shippzy.ca' || hostname === 'www.shippzy.ca';
};

/**
 * Wait for gtag to be ready
 * @returns {Promise<boolean>} True if gtag is available
 */
const waitForGtag = () => {
  return new Promise((resolve) => {
    // If gtag already exists and is not a stub, resolve immediately
    if (typeof window.gtag === 'function' && window.dataLayer) {
      // Check if it's not the stub function
      const gtagString = window.gtag.toString();
      if (!gtagString.includes('Silent stub') && gtagString.includes('dataLayer')) {
        resolve(true);
        return;
      }
    }

    // Wait for gtag to become available (max 10 seconds)
    let attempts = 0;
    const maxAttempts = 100; // 10 seconds (100 * 100ms)
    
    const checkGtag = setInterval(() => {
      attempts++;
      
      if (typeof window.gtag === 'function' && window.dataLayer) {
        const gtagString = window.gtag.toString();
        if (!gtagString.includes('Silent stub') && gtagString.includes('dataLayer')) {
          clearInterval(checkGtag);
          resolve(true);
          return;
        }
      }
      
      if (attempts >= maxAttempts) {
        clearInterval(checkGtag);
        resolve(false); // Timeout - gtag didn't load
      }
    }, 100);
  });
};

/**
 * Fire Google Ads conversion event
 * Only fires once per unique submission ID to prevent duplicates
 * Only fires on production domains (shippzy.ca, www.shippzy.ca)
 * Waits for gtag to be ready before firing
 * 
 * @param {string} submissionId - Unique identifier for this submission
 * @param {string} formType - Type of form (contact, pickup, quote)
 */
export const trackConversion = async (submissionId, formType = 'contact') => {
  // Check if on production domain
  if (!isProductionDomain()) {
    return; // Do not track on preview/dev/localhost
  }

  // Prevent duplicate conversions
  if (trackedSubmissions.has(submissionId)) {
    return;
  }

  // Wait for gtag to be ready
  const gtagReady = await waitForGtag();
  
  if (!gtagReady || typeof window.gtag !== 'function') {
    // gtag not available even after waiting
    return;
  }

  try {
    // Fire conversion event (PRODUCTION ONLY)
    window.gtag('event', 'conversion', {
      'send_to': 'AW-18389880536/MUZ6CPC7op4dENid_cBE',
      'event_category': 'form_submission',
      'event_label': formType
    });

    // Mark this submission as tracked
    trackedSubmissions.add(submissionId);
    
    // For debugging in production (temporary)
    if (window.location.search.includes('debug=ads')) {
      console.log('[Google Ads] Conversion tracked:', formType, submissionId);
    }
  } catch (error) {
    // Silent fail in production
  }
};

/**
 * Generate unique submission ID
 * Combines timestamp with form type and random component
 * 
 * @param {string} formType - Type of form being submitted
 * @returns {string} Unique submission ID
 */
export const generateSubmissionId = (formType) => {
  return `${formType}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Clear tracked submissions (useful for testing)
 * DO NOT use in production except for testing purposes
 */
export const clearTrackedSubmissions = () => {
  trackedSubmissions.clear();
};
